const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Polling config
const POLL_INTERVAL_MS = 3000;      // Check every 3 seconds
const MAX_POLL_TIME_MS = 300_000;   // Give up after 5 minutes

class VirtualTryOnService {

  // ═══════════════════════════════════════════════════════════════
  //  STEP 1: Submit the job (returns instantly with jobId)
  // ═══════════════════════════════════════════════════════════════
  static async submitTryOn(userPhoto, productId, productImage, productCategory = null) {
    const formData = new FormData();
    formData.append('userPhoto', userPhoto);
    formData.append('productId', productId);
    formData.append('productImage', productImage);
    if (productCategory) formData.append('productCategory', productCategory);

    const response = await fetch(`${API_BASE_URL}/virtual-tryon/process`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data.success || !data.data?.jobId) {
      throw new Error(data.message || 'Failed to start try-on job');
    }

    return data.data.jobId;
  }

  // ═══════════════════════════════════════════════════════════════
  //  STEP 2: Poll for job status until completed/failed
  //
  //  onProgress(progress, stage) — called on each poll with real data
  //  abortSignal — stops polling if user closes modal
  // ═══════════════════════════════════════════════════════════════
  static async pollForResult(jobId, onProgress, abortSignal = null) {
    const startTime = Date.now();

    while (true) {
      // Check if user cancelled
      if (abortSignal?.aborted) {
        throw new DOMException('Request was cancelled', 'AbortError');
      }

      // Check timeout
      if (Date.now() - startTime > MAX_POLL_TIME_MS) {
        const timeoutError = new Error('Processing is taking too long. Please try again.');
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }

      // Poll status
      const response = await fetch(`${API_BASE_URL}/virtual-tryon/status/${jobId}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Job expired. Please try again.');
        }
        throw new Error(`Status check failed: HTTP ${response.status}`);
      }

      const { data } = await response.json();

      // Report real progress from server
      if (onProgress) onProgress(data.progress, data.stage);

      // Job completed
      if (data.status === 'completed') {
        return {
          success: true,
          data: {
            tryOnId: jobId,
            imageUrl: data.imageUrl,
            processingTime: data.processingTime,
            debugInfo: data.debugInfo
          }
        };
      }

      // Job failed — pass debugInfo so frontend can show real failure point
      if (data.status === 'failed') {
        const err = new Error(data.error || 'Try-on processing failed');
        err.debugInfo = data.debugInfo;
        throw err;
      }

      // Wait before next poll
      await new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, POLL_INTERVAL_MS);
        if (abortSignal) {
          abortSignal.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(new DOMException('Request was cancelled', 'AbortError'));
          }, { once: true });
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  Combined: Submit + Poll (drop-in replacement for old processTryOn)
  // ═══════════════════════════════════════════════════════════════
  static async processTryOn(userPhoto, productId, productImage, productCategory = null, abortSignal = null, onProgress = null) {
    const requestId = `frontend_${Date.now()}`;
    console.log(`🔍 [${requestId}] ===== SUBMITTING TRY-ON JOB =====`);

    // Step 1: Submit (instant — no 504 risk)
    const jobId = await VirtualTryOnService.submitTryOn(
      userPhoto, productId, productImage, productCategory
    );
    console.log(`✅ [${requestId}] Job submitted: ${jobId}`);

    // Step 2: Poll until done (each poll is a tiny fast GET — no 504 risk)
    const result = await VirtualTryOnService.pollForResult(jobId, onProgress, abortSignal);
    console.log(`✅ [${requestId}] Completed in ${result.data.processingTime}s`);

    return result;
  }

  // Get try-on history for a user
  static async getTryOnHistory(userId) {
    const response = await fetch(`${API_BASE_URL}/virtual-tryon/history/${userId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  // Health check
  static async healthCheck() {
    const response = await fetch(`${API_BASE_URL}/virtual-tryon/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  // Validate image file — accepts ALL image formats
  static validateImageFile(file) {
    const errors = [];
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.push(`Image size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 20MB limit`);
    }
    if (!file.type.startsWith('image/')) {
      errors.push('File must be an image');
    }
    return { isValid: errors.length === 0, errors };
  }

  // Resize image if needed (client-side optimization)
  static async resizeImageIfNeeded(file, maxWidth = 2048, maxHeight = 2048) {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        let { width, height } = img;
        if (width <= maxWidth && height <= maxHeight) { resolve(file); return; }

        if (width > height) {
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
        } else {
          if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
        }, file.type, 0.85);
      };

      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  }

  static getEstimatedProcessingTime() {
    return { min: 15, max: 60, average: 35 };
  }

  static getTryOnCost() {
    return { credits: 1, estimatedCost: 0.08, currency: 'USD' };
  }
}

export default VirtualTryOnService;
